import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
  Chip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ZoomInMapIcon from "@mui/icons-material/ZoomInMap";
import RefreshIcon from "@mui/icons-material/Refresh";

/**
 * AttributeTable
 *
 * Purely presentational — renders in the space left by the shrunk map div.
 * Parent controls open/close via `layer` prop and handles map div height.
 *
 * @param {Object|null} layer   - { name, id, vectorLayer } — set to open, null to close
 * @param {Object}      map     - OL map instance (for zoom-to-feature)
 * @param {Function}    onClose
 */
export default function AttributeTable({ layer, map, onClose }) {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selectedFid, setSelectedFid] = useState(null);

  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = () => setRefreshTick(t => t + 1);

  // ── Load features when layer changes or refresh triggered ──
  useEffect(() => {
    if (!layer?.vectorLayer) {
      setRows([]);
      setColumns([]);
      setSelectedFid(null);
      return;
    }

    const features = layer.vectorLayer.getSource().getFeatures();

    if (features.length === 0) {
      setRows([]);
      setColumns([]);
      return;
    }

    const colSet = new Set();
    features.forEach(f => {
      Object.keys(f.getProperties()).forEach(k => {
        if (k !== "geometry") colSet.add(k);
      });
    });
    const cols = Array.from(colSet);
    setColumns(cols);

    setRows(
      features.map(f => ({
        _fid: f.getId() ?? f.ol_uid,
        _feature: f,
        ...Object.fromEntries(cols.map(col => [col, f.get(col) ?? ""])),
      }))
    );
    setSelectedFid(null);
  }, [layer, refreshTick]);

  // ── Zoom to feature ──
  const zoomToFeature = (feature) => {
    if (!map) return;
    const extent = feature.getGeometry()?.getExtent();
    if (!extent) return;
    map.getView().fit(extent, { padding: [60, 60, 60, 60], duration: 400, maxZoom: 18 });
  };

  if (!layer) return null;

  return (
    <Box sx={{
      flex: 1,
      minHeight: 0,                   // critical — allows flex child to shrink below content size
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      borderTop: "2px solid",
      borderColor: "primary.main",
      background: "#fff",
    }}>

      {/* ── Header ── */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1,
        px: 1.5, minHeight: 40,
        background: "#1976d2", flexShrink: 0,
      }}>
        <TableRowsIcon sx={{ fontSize: 15, color: "#fff" }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
          {layer.name}
        </Typography>
        <Chip
          label={`${rows.length} features`}
          size="small"
          sx={{
            height: 18, fontSize: 10, fontWeight: 500,
            background: "rgba(255,255,255,0.2)", color: "#fff",
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={refresh}
            sx={{ color: "#fff", p: 0.5, "&:hover": { background: "rgba(255,255,255,0.15)" } }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: "#fff", p: 0.5, "&:hover": { background: "rgba(255,255,255,0.15)" } }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Divider />

      {/* ── Table ── */}
      {rows.length === 0 ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.disabled">
            No features in this layer
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  width: 36, background: "#f5f5f5", p: 0.5,
                  position: "sticky", left: 0, zIndex: 3,
                  borderRight: "1px solid rgba(0,0,0,0.08)",
                }} />
                {columns.map(col => (
                  <TableCell key={col} sx={{
                    fontWeight: 600, fontSize: 11, whiteSpace: "nowrap",
                    background: "#f5f5f5", py: 0.75, px: 1.5,
                    color: "text.secondary", textTransform: "uppercase",
                    letterSpacing: 0.5, borderRight: "1px solid rgba(0,0,0,0.06)",
                  }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => {
                const isSelected = row._fid === selectedFid;
                return (
                  <TableRow
                    key={row._fid}
                    onClick={() => setSelectedFid(isSelected ? null : row._fid)}
                    sx={{
                      cursor: "pointer",
                      background: isSelected ? "rgba(25,118,210,0.08)" : "transparent",
                      "&:hover": { background: isSelected ? "rgba(25,118,210,0.12)" : "rgba(0,0,0,0.02)" },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell
                      sx={{
                        width: 36, p: 0.5,
                        borderRight: "1px solid rgba(0,0,0,0.08)",
                        position: "sticky", left: 0, zIndex: 1,
                        background: isSelected ? "rgba(25,118,210,0.08)" : "#fff",
                      }}
                      onClick={(e) => { e.stopPropagation(); zoomToFeature(row._feature); }}
                    >
                      <Tooltip title="Zoom to feature" placement="right">
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          <ZoomInMapIcon sx={{ fontSize: 13, color: "primary.main" }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                    {columns.map(col => (
                      <TableCell key={col} sx={{
                        fontSize: 12, py: 0.5, px: 1.5,
                        whiteSpace: "nowrap", maxWidth: 200,
                        overflow: "hidden", textOverflow: "ellipsis",
                        borderRight: "1px solid rgba(0,0,0,0.04)",
                        color: row[col] === "" ? "text.disabled" : "text.primary",
                        fontStyle: row[col] === "" ? "italic" : "normal",
                      }}>
                        {row[col] === "" ? "—" : String(row[col])}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}